import React, { useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import ModuleLayout from '../../../layouts/ModuleLayout';
import TabContainer from '../../../components/navigation/TabContainer';
import Drawer from '../../../components/common/Drawer';
import Button from '../../../components/common/Button/Button';
import { Plus } from 'lucide-react';
import PermissionGate from '../../../components/common/PermissionGate';

// Import Tab Components
import EmployeeList from './EmployeeList';
import BulkUpload from './BulkUpload';

// Import Analytics
import HRAnalyticsDashboard from '../analytics-reporting/HRAnalyticsDashboard';
import AttendanceKpiDashboard from '../attendance-leaves/AttendanceKpiDashboard';

// Import Drawer Components
import AddEmployee from './AddEmployee';
import EditEmployee from './EditEmployee';
import EmployeeProfile from './EmployeeProfile';

const EmployeeModule = ({ initialTab, initialAction, initialActionId }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();

    // Determine active tab from URL or props
    const activeTab = searchParams.get('tab') || initialTab || 'employeeList';

    // Drawer state from URL or props
    const action = searchParams.get('action') || initialAction;
    const actionId = searchParams.get('id') || initialActionId;

    // Tab Registry
    const tabRegistry = {
        employeeList: (props) => (
            <EmployeeList
                {...props}
                onViewProfile={(id) => navigate(`/employee_profile/${id}`, { state: { from: location.pathname + location.search } })}
                onEditEmployee={(id) => navigate(`/edit_employee/${id}`, { state: { from: location.pathname + location.search } })}
                onAddEmployee={() => navigate('/add_employee', { state: { from: location.pathname + location.search } })}
            />
        ),
        addEmployee: AddEmployee, // Legacy tab support
        bulkUpload: BulkUpload,
        analytics: HRAnalyticsDashboard,
        attendanceKpi: AttendanceKpiDashboard
    };

    // Tab Configuration for Layout
    const tabs = [
        { id: 'employeeList', label: 'Employee List' },
        { id: 'bulkUpload', label: 'Bulk Import' },
        { id: 'attendanceKpi', label: 'Attendance KPI' },
        { id: 'analytics', label: 'Employee Analytics' },
    ];

    // Helper to update params smoothly
    const updateParams = (updates) => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            Object.entries(updates).forEach(([key, value]) => {
                if (value === null || value === undefined) {
                    newParams.delete(key);
                } else {
                    newParams.set(key, value);
                }
            });
            return newParams;
        });
    };

    // Handlers
    const handleTabChange = (tabId) => {
        setSearchParams({ tab: tabId });
    };

    const handleCloseDrawer = () => {
        setSearchParams(prev => {
            const newParams = new URLSearchParams(prev);
            newParams.delete('action');
            newParams.delete('id');
            return newParams;
        });
    };

    // Primary Action Logic (Header Button)
    const getPrimaryAction = () => {
        return null;
    };

    // Drawer Content Logic
    const renderDrawerContent = () => {
        if (!action) return null;

        switch (action) {
            case 'addEmployee':
                return (
                    <AddEmployee
                        onClose={handleCloseDrawer}
                        onSuccess={() => {
                            handleCloseDrawer();
                            // Optional: Refresh list mechanism if needed, 
                            // but react-query or state lift might handle it.
                            // For now, simple close. 
                        }}
                    />
                );
            case 'editEmployee':
                return (
                    <AddEmployee
                        id={actionId} // Passing ID to AddEmployee to trigger edit mode
                        onClose={handleCloseDrawer}
                        onSuccess={handleCloseDrawer}
                    />
                );
            case 'profile':
                return (
                    <EmployeeProfile
                        id={actionId}
                        onClose={handleCloseDrawer}
                        onEdit={() => navigate(`/edit_employee/${actionId}`)}
                    />
                );
            default:
                return (
                    <div className="p-4 text-red-500">
                        Unknown action: {action}
                    </div>
                );
        }
    };

    const getDrawerTitle = () => {
        switch (action) {
            case 'addEmployee': return 'Add New Employee';
            case 'editEmployee': return 'Edit Employee Details';
            case 'profile': return 'Profile';
            default: return '';
        }
    };

    // Drawer Size Logic
    const getDrawerSize = () => {
        switch (action) {
            case 'profile': return 'large'; // Right-side drawer for profile
            default: return 'full'; // Full screen for forms
        }
    };

    return (
        <ModuleLayout
            title="Employee Database"
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            action={getPrimaryAction()}
        >
            <TabContainer
                activeTab={activeTab}
                tabRegistry={tabRegistry}
                // Pass extra props to active tab component if needed
                additionalProps={{
                    onViewProfile: (id) => navigate(`/employee_profile/${id}`, { state: { from: location.pathname + location.search } }),
                    onEditEmployee: (id) => navigate(`/edit_employee/${id}`, { state: { from: location.pathname + location.search } })
                }}
            />

            {/* Action Drawer */}
            <Drawer
                isOpen={!!action}
                onClose={handleCloseDrawer}
                title={getDrawerTitle()}
                size={getDrawerSize()}
                hideCloseButton={false}
            >
                {renderDrawerContent()}
            </Drawer>
        </ModuleLayout>
    );
};

export default EmployeeModule;
